import { Component, inject, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { deepCopy } from '@app/api/core/utils/deep-copy.utils';
import { AbstractReactiveDialogComponent } from '@app/shared/components/dialog/reactive-dialog/base/abstract-reactive-dialog-component';
import { ReactiveDialogContentComponent } from '@app/shared/components/dialog/reactive-dialog/base/reactive-dialog-content/reactive-dialog-content.component';
import { DataService } from '@app/shared/services/data/data.service';
import { MessageType } from '@app/shared/services/ui/message/message';
import { MessageService } from '@app/shared/services/ui/message/message.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FieldProperty, Reminder } from '@o30/entities';
import { EMPTY, of } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { BasicDatepickerComponent } from '../../form/basic-datepicker/basic-datepicker.component';
import { BasicTextareaComponent } from '../../form/basic-textarea/basic-textarea.component';

@UntilDestroy()
@Component({
  selector:    'app-reminder-dialog',
  templateUrl: './reminder-dialog.component.html',
  styleUrls:   ['reminder-dilaog.component.scss'],
  standalone:  true,
  imports:     [
    ReactiveDialogContentComponent,
    BasicTextareaComponent,
    FormsModule,
    ReactiveFormsModule,
    BasicDatepickerComponent,
    MatButton
  ]
})
export class ReminderDialogComponent extends AbstractReactiveDialogComponent<Reminder, Reminder, number> implements OnInit {
  private dataService = inject(DataService);
  private messageService = inject(MessageService);

  formGroup: UntypedFormGroup;

  constructor() {
    super();
  }

  ngOnInit(): void {
    const fb = new UntypedFormBuilder();
    this.formGroup = fb.group({
      text: this?.reminder?.text?.value,
      date: this?.reminder?.date?.value
    });
  }

  get reminder() {
    return this.data;
  }

  get polizzeId() {
    return this.context;
  }

  getFormControl(key: keyof Reminder) {
    return this?.formGroup?.get(key) as UntypedFormControl;
  }

  onSaveClick() {
    const reminder = this.reminder ? deepCopy(this.reminder) : {};
    this.assignValue(reminder, 'text');
    this.assignValue(reminder, 'date');

    this.dataService.putPolizzeReminder(this.polizzeId, reminder)
      .pipe(
        untilDestroyed(this), take(1),
        switchMap(_payload => this.messageService.handlePayloadReturnSuccess(_payload) ? of(_payload) : EMPTY)
      )
      .subscribe(_payload => {
        this.messageService.showSnackBar({
          text: 'Erinnerung gespeichert.',
          type: MessageType.INFO
        });
        this.close(reminder);
      });
  }

  onDeleteClick() {
    this.dataService.deletePolizzeReminder(this.polizzeId)
      .pipe(
        untilDestroyed(this), take(1),
        switchMap(_payload => this.messageService.handlePayloadReturnSuccess(_payload) ? of(_payload) : EMPTY)
      )
      .subscribe(_payload => {
        this.messageService.showSnackBar({
          text: 'Erinnerung gelöscht.',
          type: MessageType.INFO
        });
        this.close(null);
      });
  }

  private assignValue(reminder: Reminder, key: keyof Omit<Reminder, 'id' | 'polizzennummer'>) {
    reminder[key] = {value: this.formGroup.get(key).value} as FieldProperty<string>;
  }
}
